import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faFilter, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';

export interface FilterOption { value: string; label: string; count?: number }

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];              // множественный выбор; пусто = все
  onChange: (next: string[]) => void;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  // Одиночный фильтр (легаси-режим)
  options?: FilterOption[];
  filterValue?: string;
  onFilter?: (v: string) => void;
  // Мульти-фильтр (несколько групп, множественный выбор)
  groups?: FilterGroup[];
}

// Строка поиска со встроенным фильтром.
// Один фильтр → выпадающий список. Несколько групп → компактная панель-аккордеон с мультивыбором.
const MobileSearchFilter: React.FC<Props> = ({ value, onChange, placeholder, options, filterValue, onFilter, groups }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const hasGroups = !!groups && groups.length > 0;
  const hasSingle = !hasGroups && !!options && options.length > 0;

  // легаси (один фильтр)
  const active = hasSingle && filterValue !== undefined && filterValue !== options![0].value;
  const current = hasSingle ? options!.find((o) => o.value === filterValue) : undefined;
  const singleLabel = current ? current.label : (t('Filter') as string);

  // мульти-группы
  const activeCount = hasGroups ? groups!.filter((g) => g.selected.length > 0).length : 0;
  const resetAll = () => groups!.forEach((g) => g.selected.length && g.onChange([]));
  const toggleValue = (g: FilterGroup, v: string) =>
    g.onChange(g.selected.includes(v) ? g.selected.filter((x) => x !== v) : [...g.selected, v]);
  const summary = (g: FilterGroup): string => {
    if (g.selected.length === 0) return t('All') as string;
    const first = g.options.find((o) => o.value === g.selected[0])?.label ?? g.selected[0];
    return g.selected.length > 1 ? `${first} +${g.selected.length - 1}` : first;
  };

  return (
    <div className="m-sf">
      <div className="m-sf-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="m-sf-ic" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || `${t('Search')}…`}
        />
      </div>

      {/* Легаси: одиночный фильтр — выпадающий список */}
      {hasSingle && (
        <div className="m-sf-filter">
          <button type="button" className={`m-sf-btn${active ? ' on' : ''}`} onClick={() => setOpen((o) => !o)}>
            <FontAwesomeIcon icon={faFilter} />
            <span className="m-sf-btn-lbl">{singleLabel}</span>
          </button>
          {open && (
            <>
              <div className="m-sf-scrim" onClick={() => setOpen(false)} />
              <div className="m-sf-menu">
                {options!.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    className={`m-sf-item${filterValue === o.value ? ' sel' : ''}`}
                    onClick={() => { onFilter?.(o.value); setOpen(false); }}
                  >
                    <span className="m-sf-item-lbl">{o.label}</span>
                    {o.count !== undefined && <span className="m-sf-cnt">{o.count}</span>}
                    {filterValue === o.value && <FontAwesomeIcon icon={faCheck} className="m-sf-tick" />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Мульти-группы — компактная панель-аккордеон с мультивыбором */}
      {hasGroups && (
        <div className="m-sf-filter">
          <button type="button" className={`m-sf-btn${activeCount ? ' on' : ''}`} onClick={() => setOpen(true)}>
            <FontAwesomeIcon icon={faFilter} />
            <span className="m-sf-btn-lbl">{t('Filter')}{activeCount ? ` · ${activeCount}` : ''}</span>
          </button>
          {open && (
            <>
              <div className="m-sheet-scrim" onClick={() => setOpen(false)} />
              <div className="m-sheet m-sf-sheet">
                <div className="m-sheet-grab" />
                <div className="m-sheet-head">
                  <div className="m-sheet-title">{t('Filters')}</div>
                  {activeCount > 0 && <button type="button" className="m-sf-reset" onClick={resetAll}>{t('ResetFilters')}</button>}
                </div>
                <div className="m-sheet-body m-sf-acc-list">
                  {groups!.map((g) => {
                    const isOpen = openKey === g.key;
                    const on = g.selected.length > 0;
                    return (
                      <div key={g.key} className={`m-sf-acc${isOpen ? ' open' : ''}`}>
                        <button type="button" className="m-sf-acc-head" onClick={() => setOpenKey(isOpen ? null : g.key)}>
                          <span className="m-sf-acc-lbl">{g.label}</span>
                          <span className={`m-sf-acc-sum${on ? ' on' : ''}`}>{summary(g)}</span>
                          <FontAwesomeIcon icon={faChevronDown} className={`m-sf-acc-chev${isOpen ? ' open' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="m-sf-acc-body">
                            {g.options.map((o) => {
                              const sel = g.selected.includes(o.value);
                              return (
                                <button type="button" key={o.value} className={`m-sf-opt${sel ? ' sel' : ''}`} onClick={() => toggleValue(g, o.value)}>
                                  <span className="m-sf-opt-box">{sel && <FontAwesomeIcon icon={faCheck} />}</span>
                                  <span className="m-sf-opt-lbl">{o.label}</span>
                                  {o.count !== undefined && <span className="m-sf-cnt">{o.count}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="btn-primary m-sf-done" onClick={() => setOpen(false)}>{t('Done')}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileSearchFilter;
